import React, { useEffect, useState, useCallback } from 'react';
import { collection, getDocs } from 'firebase/firestore';
import { db } from '../db/firebase';
import { useNavigate } from 'react-router-dom';

// Define the specific category order you want
const CATEGORY_ORDER = [
  'Executive Men Shoes',
  'Regular Men Shoe', 
  'Women Shoes'
  // The remaining categories will appear after these in any order
];

// Individual Category Tile Component - Memoized to prevent re-renders
const CategoryTile = React.memo(({ name, image, link, categoryId, onCategoryClick }) => {
  const [imgError, setImgError] = useState(false);
  const [imgLoaded, setImgLoaded] = useState(false);
  
  const handleImageError = () => {
    setImgError(true);
  };

  const handleImageLoad = () => {
    setImgLoaded(true);
  };

  const handleClick = (e) => {
    e.preventDefault();
    onCategoryClick(categoryId, name);
  };

  return (
    <a 
      href={link} 
      className="relative block h-40 lg:h-42 overflow-hidden group cursor-pointer"
      onClick={handleClick}
    >
      {/* Background Image with Overlay - Only show if image exists and loaded successfully */}
      {image && !imgError ? (
        <>
          <img
            src={image}
            alt={name}
            className={`absolute inset-0 w-full h-full object-cover 
                       transition-transform duration-500 ease-in-out 
                       group-hover:scale-105 ${imgLoaded ? 'opacity-100' : 'opacity-0'}`}
            onError={handleImageError}
            onLoad={handleImageLoad}
            loading="lazy"
          />
          
          {/* Loading skeleton for image */}
          {!imgLoaded && (
            <div className="absolute inset-0 bg-gray-200 animate-pulse flex items-center justify-center">
              <div className="text-gray-400">
                <svg className="w-8 h-8 animate-spin" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                </svg>
              </div>
            </div>
          )}
        </>
      ) : (
        /* No image available state */
        <div className="absolute inset-0 bg-gray-200 flex items-center justify-center">
          <div className="text-center text-gray-500">
            <svg className="w-12 h-12 mx-auto mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
            </svg>
            <p className="text-sm font-medium">No Image</p>
          </div>
        </div>
      )}
      
      {/* Gradient/Dark Overlay for better text readability */}
      <div className="absolute inset-0 bg-black/50 transition-colors group-hover:bg-black/40"></div>
      
      {/* Text Content Container */}
      <div className="absolute bottom-4 left-6 right-6 text-white">
        <p className="text-xs font-semibold opacity-80 mb-1">Category</p>
        <h2 className="text-3xl font-bold mb-2">{name}</h2>
        
        <button className="bg-taupe text-sm font-medium text-white px-4 py-2 
                           rounded-sm shadow-lg transition-colors 
                           hover:bg-primary-dark cursor-pointer">
          Explore Now
        </button>
      </div>
    </a>
  );
});

CategoryTile.displayName = 'CategoryTile';

const CategoryGrid = () => {
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const navigate = useNavigate();

  // Function to sort categories according to specified order
  const sortCategories = useCallback((categories) => {
    return categories.sort((a, b) => {
      const indexA = CATEGORY_ORDER.indexOf(a.name);
      const indexB = CATEGORY_ORDER.indexOf(b.name);
      
      // If both are in the priority list, sort by the defined order
      if (indexA !== -1 && indexB !== -1) {
        return indexA - indexB;
      }
      // If only A is in priority list, A comes first
      if (indexA !== -1) return -1;
      // If only B is in priority list, B comes first
      if (indexB !== -1) return 1;
      // If neither are in priority list, maintain original order
      return a.name.localeCompare(b.name); // Sort alphabetically
    });
  }, []);

  // Handle category click navigation
  const handleCategoryClick = useCallback((categoryId, categoryName) => {
    navigate(`/shop/category/${categoryId}`);
  }, [navigate]);

  // Optimized fetch function with caching - Only fetches main categories
  const fetchCategories = useCallback(async () => {
    // Simple cache check to prevent redundant fetches
    const cacheKey = 'main_categories_cache';
    const cached = sessionStorage.getItem(cacheKey);
    
    if (cached && !error) {
      const cachedData = JSON.parse(cached);
      if (Date.now() - cachedData.timestamp < 5 * 60 * 1000) { // 5 minute cache
        setCategories(cachedData.categories);
        setLoading(false);
        return;
      }
    }

    try {
      setLoading(true);
      setError(null);
      
      const categoriesSnapshot = await getDocs(collection(db, 'categories'));
      
      if (categoriesSnapshot.empty) {
        console.log('No categories found in Firebase');
        setCategories([]);
        setLoading(false);
        return;
      }

      // Filter for main categories only (categories without ParentCategory)
      const mainCategories = categoriesSnapshot.docs.filter(doc => {
        const categoryData = doc.data();
        return !categoryData.ParentCategory; // Only include categories without parent
      });

      console.log(`Found ${mainCategories.length} main categories out of ${categoriesSnapshot.docs.length} total categories`);

      // Process main categories
      const categoriesData = mainCategories.map((doc) => {
        const categoryData = doc.data();
        const categoryId = doc.id;
        const categoryName = categoryData.CategoryName || 'Unnamed Category';
        
        return {
          id: categoryId,
          name: categoryName,
          image: categoryData.CatImgUrl || null,
          link: `/shop/category/${categoryId}`,
          isMainCategory: true
        };
      });

      // Sort categories according to specified order
      const sortedCategories = sortCategories(categoriesData);

      // Cache the results
      const cacheData = {
        categories: sortedCategories,
        timestamp: Date.now()
      };
      sessionStorage.setItem(cacheKey, JSON.stringify(cacheData));

      setCategories(sortedCategories);
      
    } catch (err) {
      console.error('Error fetching categories:', err);
      setError('Failed to load categories');
      setCategories([]); // Ensure categories is empty on error
    } finally {
      setLoading(false);
    }
  }, [error, sortCategories]);

  useEffect(() => {
    fetchCategories();
  }, [fetchCategories]);

  // Loading state with better skeleton
  if (loading) {
    return (
      <section className="py-2 md:py-4 bg-offwhite">
        <div className="max-w-7xl mx-auto px-4 sm:px-4 lg:px-6">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
            {[...Array(6)].map((_, index) => (
              <div key={index} className="relative block h-40 lg:h-42 overflow-hidden bg-gray-200 animate-pulse">
                <div className="absolute inset-0 bg-gray-300"></div>
                <div className="absolute bottom-4 left-6 right-6">
                  <div className="h-4 bg-gray-400 rounded w-20 mb-2"></div>
                  <div className="h-8 bg-gray-400 rounded w-32 mb-3"></div>
                  <div className="h-8 bg-gray-400 rounded w-24"></div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>
    );
  }

  // Error state
  if (error) {
    return (
      <section className="py-2 md:py-4 bg-offwhite">
        <div className="max-w-7xl mx-auto px-4 sm:px-4 lg:px-6 text-center">
          <div className="bg-red-50 border border-red-200 rounded-lg p-4">
            <p className="text-red-800">{error}</p>
            <button 
              onClick={fetchCategories}
              className="mt-2 bg-red-600 text-white px-4 py-2 rounded hover:bg-red-700 transition-colors"
            >
              Retry
            </button>
          </div>
        </div>
      </section>
    );
  }

  // Empty state - when no categories are found
  if (categories.length === 0) {
    return (
      <section className="py-2 md:py-4 bg-offwhite">
        <div className="max-w-7xl mx-auto px-4 sm:px-4 lg:px-6 text-center">
          <div className="bg-gray-50 border border-gray-200 rounded-lg p-8">
            <svg className="w-16 h-16 text-gray-400 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2M4 13h2m8-8V4a1 1 0 00-1-1h-2a1 1 0 00-1 1v1M9 7h6" />
            </svg>
            <h3 className="text-lg font-medium text-gray-900 mb-2">No Main Categories Found</h3>
            <p className="text-gray-600 mb-4">There are no main product categories available at the moment.</p>
            <button 
              onClick={fetchCategories}
              className="bg-primary text-white px-4 py-2 rounded hover:bg-primary-dark transition-colors"
            >
              Refresh Categories
            </button>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className="py-2 md:py-4 bg-offwhite"> 
      <div className="max-w-7xl mx-auto px-4 sm:px-4 lg:px-6">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
          {categories.map((category) => (
            <CategoryTile
              key={category.id}
              name={category.name}
              image={category.image}
              link={category.link}
              categoryId={category.id}
              onCategoryClick={handleCategoryClick}
            />
          ))}
        </div>
      </div>
    </section>
  );
};

export default CategoryGrid;